import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';

// Node interface for campaign slots
export interface SlotNode {
  id: string;
  name: string;
  location: string;
  price: number;
  quantity: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Node interface for admin management (extends SlotNode)
export interface AdminNode extends SlotNode {
  status: 'Online' | 'Offline' | 'Error';
  lastActive: string;
  uptime: string;
  banner: string;
  statusColor: 'green' | 'red' | 'yellow';
}

interface NodeContextType {
  // Slot nodes for campaign builder
  slotNodes: SlotNode[];
  
  // Admin nodes for admin management
  adminNodes: AdminNode[];
  
  // CRUD operations
  addNode: (node: Omit<SlotNode, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateNode: (id: string, updates: Partial<SlotNode>) => void;
  deleteNode: (id: string) => void;
  
  // Admin specific operations
  updateNodeStatus: (id: string, status: AdminNode['status']) => void;
  
  // Loading state
  loading: boolean;
  error: string | null;
}

const NodeContext = createContext<NodeContextType | undefined>(undefined);

// Initial slot nodes data
const INITIAL_SLOT_NODES: SlotNode[] = [
  {
    id: 'node-a',
    name: 'Node A',
    location: 'Kota ACON Shah Alam',
    price: 80,
    quantity: 1,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'node-b',
    name: 'Node B',
    location: 'Kota ACON Shah Alam',
    price: 120,
    quantity: 1,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'node-c',
    name: 'Node C',
    location: 'Kota ACON Shah Alam',
    price: 150,
    quantity: 1,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'node-d',
    name: 'Node D',
    location: 'Kota ACON Shah Alam',
    price: 300,
    quantity: 1,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// Helper function to convert SlotNode to AdminNode
const slotNodeToAdminNode = (slotNode: SlotNode): AdminNode => ({
  ...slotNode,
  status: slotNode.isActive ? 'Online' : 'Offline',
  lastActive: '2m ago',
  uptime: '99.8%',
  banner: 'Current Ad Running',
  statusColor: slotNode.isActive ? 'green' : 'red',
});

interface NodeProviderProps {
  children: ReactNode;
}

export const NodeProvider: React.FC<NodeProviderProps> = ({ children }) => {
  const [slotNodes, setSlotNodes] = useState<SlotNode[]>(INITIAL_SLOT_NODES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Convert slot nodes to admin nodes with additional status info
  const adminNodes: AdminNode[] = slotNodes.map(slotNodeToAdminNode);

  // Generate unique ID
  const generateId = () => `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const addNode = (nodeData: Omit<SlotNode, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      setLoading(true);
      const newNode: SlotNode = {
        ...nodeData,
        id: generateId(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      setSlotNodes(prev => [...prev, newNode]);
      setError(null);
    } catch (err) {
      setError('Failed to add node');
      console.error('Error adding node:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateNode = (id: string, updates: Partial<SlotNode>) => {
    try {
      setLoading(true);
      setSlotNodes(prev => 
        prev.map(node => 
          node.id === id 
            ? { ...node, ...updates, updatedAt: new Date().toISOString() }
            : node
        )
      );
      setError(null);
    } catch (err) {
      setError('Failed to update node');
      console.error('Error updating node:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteNode = (id: string) => {
    try {
      setLoading(true);
      setSlotNodes(prev => prev.filter(node => node.id !== id));
      setError(null);
    } catch (err) {
      setError('Failed to delete node');
      console.error('Error deleting node:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateNodeStatus = (id: string, status: AdminNode['status']) => {
    const isActive = status === 'Online';
    updateNode(id, { isActive });
  };

  // Simulate data persistence (in a real app, this would sync with a backend)
  useEffect(() => {
    const savedNodes = localStorage.getItem('lookal-nodes');
    if (savedNodes) {
      try {
        const parsedNodes = JSON.parse(savedNodes);
        setSlotNodes(parsedNodes);
      } catch (err) {
        console.error('Error loading saved nodes:', err);
      }
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem('lookal-nodes', JSON.stringify(slotNodes));
    }, 300);
    return () => clearTimeout(timer);
  }, [slotNodes]);

  const value = useMemo<NodeContextType>(() => ({
    slotNodes,
    adminNodes,
    addNode,
    updateNode,
    deleteNode,
    updateNodeStatus,
    loading,
    error,
  }), [slotNodes, adminNodes, addNode, updateNode, deleteNode, updateNodeStatus, loading, error]);

  return (
    <NodeContext.Provider value={value}>
      {children}
    </NodeContext.Provider>
  );
};

// Custom hook to use the node context
export const useNodes = (): NodeContextType => {
  const context = useContext(NodeContext);
  if (context === undefined) {
    throw new Error('useNodes must be used within a NodeProvider');
  }
  return context;
};

export default NodeContext;
